import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { PlayerState, PlayerSide } from '../types/dualGame';

interface PlayerBoardProps {
  player: PlayerState;
  side: PlayerSide;
  isWinner?: boolean;
  isLoser?: boolean;
  isLeading?: boolean;
  inputFlash?: boolean;
}

export const PlayerBoard: React.FC<PlayerBoardProps> = ({
  player,
  side,
  isWinner = false,
  isLoser = false,
  isLeading = false,
  inputFlash = false,
}) => {
  const { board, tiles, score, bestScore, isDead } = player;
  const size = board.length;

  const [scorePop, setScorePop] = useState<{ value: number; key: number } | null>(null);
  const prevScoreRef = useRef(score);
  const popKey = useRef(0);

  useEffect(() => {
    const delta = score - prevScoreRef.current;
    if (delta > 0) {
      popKey.current++;
      setScorePop({ value: delta, key: popKey.current });
      const timer = setTimeout(() => setScorePop(null), 800);
      prevScoreRef.current = score;
      return () => clearTimeout(timer);
    }
    prevScoreRef.current = score;
  }, [score]);

  const { cellSize, gap } = useMemo(() => {
    const cs = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--cell-size')?.trim() || '80') || 80;
    const g = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--cell-gap')?.trim() || '8') || 8;
    return { cellSize: cs, gap: g };
  }, [size]);

  const classes = ['player-board', side];
  if (isWinner) classes.push('winner');
  if (isLoser) classes.push('loser');
  if (isDead) classes.push('dead');
  if (isLeading) classes.push('leading');
  if (inputFlash) classes.push('input-flash');

  const getTileColorClass = (value: number): string => {
    if (value >= 4096) return 'tile-super';
    return `tile-${value}`;
  };

  return (
    <div className={classes.join(' ')}>
      <div className="player-header" style={{ position: 'relative' }}>
        <div className="player-title">
          <span className={`player-indicator ${side}`} />
          <span className="player-name">{side === 'left' ? '左玩家' : '右玩家'}</span>
          {isDead && <span className="dead-badge">已失败</span>}
        </div>

        <div className="score-display">
          <div className="current-score">
            <span className="label">分数</span>
            <span className="value">{score}</span>
          </div>
          <div className="best-score">
            <span className="label">最佳</span>
            <span className="value">{bestScore}</span>
          </div>
        </div>

        {scorePop && (
          <span className="score-pop" key={scorePop.key}>+{scorePop.value}</span>
        )}
      </div>

      <div className={`game-board ${isDead ? 'disabled' : ''}`}>
        <div
          className="board-grid"
          style={{
            gridTemplateColumns: `repeat(${size}, var(--cell-size))`,
            gridTemplateRows: `repeat(${size}, var(--cell-size))`,
          }}
        >
          {Array.from({ length: size * size }, (_, i) => (
            <div key={i} className="board-cell" />
          ))}
        </div>

        <div className="tiles-container">
          {tiles.map(tile => {
            const x = tile.col * (cellSize + gap);
            const y = tile.row * (cellSize + gap);
            const tileClasses = ['tile', getTileColorClass(tile.value)];
            if (tile.isNew) tileClasses.push('tile-new');
            if (tile.isMerged) tileClasses.push('tile-merged');

            return (
              <div
                key={tile.id}
                className={tileClasses.join(' ')}
                style={{
                  '--tile-pos': `translate(${x}px, ${y}px)`,
                  transform: `translate(${x}px, ${y}px)`,
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                } as React.CSSProperties}
              >
                {tile.value}
              </div>
            );
          })}
        </div>

        {isDead && (
          <div className="dead-overlay">
            <span className="dead-text">已失败</span>
          </div>
        )}
      </div>

      <div className="controls-hint">
        <span className="keys">{side === 'left' ? 'WASD' : '↑↓←→'}</span>
        <span className="label">控制</span>
      </div>
    </div>
  );
};

export default PlayerBoard;
